import { JURISDICTION_TYPE_E } from "./jurisdictions.types";
import { IResponsePagination } from "./pagination.types";

export enum PERMISSION_TYPE_E {
	DASHBOARD = "dashboard",
	VIOLATION_LOGS = "violation_logs",
	RTO_METADATA = "rto_metadata",
	INTERCEPTOR_VEHICLES = "interceptor_vehicles",
	USER_MANAGEMENT = "user_management",
	RESET_USER_ACCOUNT = "reset_user_account",
	USER_ACTIVITY_LOGS = "user_activity_logs",
	SYSTEM_CONFIGURATION = "system_configuration",
	USER_SESSION_MANAGEMENT = "user_session_management"
}

export interface IGetUsersListResponseData {
	id: number;
	full_name: string;
	first_name: string;
	last_name: string;
	username: string;
	role: number;
	email: string;
	phone_number: string;
	is_active: boolean;
	jurisdiction: number[];
	designation: string | null;
	last_login: string | null;
	validity_start_date: string | null;
	validity_end_date: string | null;
	groups: number[];
	use_group_permissions_only: boolean;
	employee_id: string;
	challan_user_id: number;
	district_id: number;
	groups_permissions: {
		[key in PERMISSION_TYPE_E]: {
			view: boolean;
			edit: boolean;
		};
	};
	user_permissions: {
		[key in PERMISSION_TYPE_E]: {
			view: boolean;
			edit: boolean;
		};
	};
}

export type IGetUsersListResponse = IResponsePagination<IGetUsersListResponseData>;

export interface IGetUsersListRequestData {
	page: number;
	search?: string;
}

export interface ICreateUserRequestData {
	first_name: string;
	last_name: string;
	// role: number;
	username: string;
	email: string;
	phone_number: string;
	designation: string;
	is_active: boolean;
	enable_ai_search: boolean;
	// password: string;
	// password2: string;
	user_permissions?: {
		[key in PERMISSION_TYPE_E]?: {
			view: boolean;
			edit: boolean;
		};
	};
	jurisdiction?: number[];
	groups: number[];
	validity_start_date: string;
	validity_end_date: string;
	use_group_permissions_only: boolean;
	employee_id: string;
	challan_user_id?: number;
	district_id?: number;
}

export interface IEditUserRequestData {
	id: number;
	data: {
		first_name?: string;
		last_name?: string;
		role?: number;
		username?: string;
		email?: string;
		phone_number?: string;
		designation?: string;
		is_active?: boolean;
		enable_ai_search?: boolean;
		// password: string;
		// password2: string;
		user_permissions?: {
			[key in PERMISSION_TYPE_E]?: {
				view: boolean;
				edit: boolean;
			};
		};
		jurisdiction?: number[];
		groups?: number[];
		validity_start_date?: string;
		validity_end_date?: string;
		use_group_permissions_only?: boolean;
		employee_id?: string;
		challan_user_id?: number;
		district_id?: number;
	};
}

export interface IGetActiveSessionsResponseData {
	id: number;
	first_name: string;
	last_name: string;
	full_name: string;
	username: string;
	role: number;
	phone_number: string;
	email: string;
	designation: string;
	groups: number[];
	jurisdiction: number;
	is_active: boolean;
	address: string;
	validity_start_date: string | null;
	validity_end_date: string | null;
	date_joined: string;
	last_login: string;
	is_superuser: boolean;
	is_staff: boolean;
	ignore: boolean;
	created_at: string;
	updated_at: string;
	user_permissions: {
		[key in PERMISSION_TYPE_E]: {
			view: boolean;
			edit: boolean;
		};
	};
	groups_permissions: {
		[key in PERMISSION_TYPE_E]: {
			view: boolean;
			edit: boolean;
		};
	};
}

export type IGetActiveSessionsResponse = IResponsePagination<IGetActiveSessionsResponseData>;

export interface IGetActiveSessionsRequestData {
	page: number;
	search?: string;
}

export interface IActivityUserDetails {
	role: number;
	email: string;
	token: string;
	customer: null;
	group_id: null;
	username: string;
	full_name: string;
	group_name: string;
	enable_ai_search: boolean;
	user_permissions: {
		[key in PERMISSION_TYPE_E]: { edit: boolean; view: boolean };
	};
	group_permissions: {
		[key in PERMISSION_TYPE_E]: { edit: boolean; view: boolean };
	};
}

export interface IActivityLogUserDetails {
	id: number;
	first_name: string;
	last_name: string;
	address: string | null;
	phone_number: string;
	last_login: string | null;
	date_joined: string;
	role: number;
	customer: [];
	designation: string | null;
	email: string;
	is_active: boolean;
	user_settings: {
		[key in "logs" | "alerts" | "traffic" | "challans"]: {
			[key in string]: boolean;
		};
	};
	username: string;
}

export interface IActivityLogJurisdictionDescendantDetails {
	id: number;
	name: string;
	pincode: number | null;
	location_co_ordinates: string | null;
	jurisdiction_type: JURISDICTION_TYPE_E;
	parent: number;
	description: string;
	rto_code: string | null;
}

export interface IActivityLogJurisdictionDetails {
	id: number;
	name: string;
	pincode: string;
	location_co_ordinates: string | null;
	jurisdiction_type: {
		id: number;
		name: "STATE" | "JURISDICTION" | "RTOS";
	};
	parent: null;
	description: string;
	rto_code: string | null;
	descendants: IActivityLogJurisdictionDescendantDetails[];
}

export interface IGetUserActivityLogsResponseData {
	id: number;
	event_type: "security" | "application_process" | "user" | "configuration_changes";
	message: string;
	timestamp: string;
	performed_by: number | null;
	performed_by_details: IActivityLogUserDetails | null;
	performed_on: number;
	performed_on_details: IActivityLogUserDetails | null;
	old_config: Record<string, any> | null;
	new_config: Record<string, any> | null;
	severity: "low" | "medium" | "high" | null;
	source: string;
	jurisdictions: number[];
	jurisdictions_details: IActivityLogJurisdictionDetails[];
	severity_display: string;
	event_type_display: string;
}

export type IGetUserActivityLogsResponse = IResponsePagination<IGetUserActivityLogsResponseData>;

export interface IGetUserActivityLogsRequestData {
	page: number;
	search?: string;
}

export interface IChangeUserPasswordRequestData {
	id: number;
	data: {
		// current_password: string;
		password: string;
		password2: string;
	};
}

export type PermissionDetailsType = {
	[key in PERMISSION_TYPE_E]: {
		view: boolean;
		edit: boolean;
	};
};
